import { isScopedPermission, normalizeGrants, type PermissionKey, type Scope } from '@erp/shared';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { grantOf } from '../../lib/session.ts';
import { PERMISSION_GROUPS, permissionLabel, SCOPE_LABELS } from '../../lib/permission-texts.ts';
import { Button } from '../ui/button.tsx';
import { Field, Input } from '../ui/field.tsx';

type Value = Scope | 'on' | 'none';
const SCOPE_OPTIONS: (Scope | 'none')[] = ['none', 'own', 'team', 'all'];
const ADMIN_KEYS = new Set<PermissionKey>([
  'users.manage',
  'roles.manage',
  'settings.manage',
  'api_keys.manage',
  'privacy.manage',
  'users.impersonate',
]);

/** Grants ("records.view:team", "users.manage") → editor state, and back. */
export function toValues(grants: readonly string[]): Map<PermissionKey, Value> {
  const values = new Map<PermissionKey, Value>();
  for (const group of PERMISSION_GROUPS) {
    for (const { key } of group.items) {
      const grant = grantOf(grants, key);
      values.set(key, grant === undefined ? 'none' : (grant ?? 'on'));
    }
  }
  return values;
}

export function toGrants(values: Map<PermissionKey, Value>): string[] {
  const grants: string[] = [];
  for (const [key, value] of values) {
    if (value === 'none') continue;
    grants.push(value === 'on' ? key : `${key}:${value}`);
  }
  return normalizeGrants(grants);
}

/**
 * Permission matrix: scoped permissions as "Ninguno / Propios / Equipo / Todos",
 * the rest as on/off. Radio groups and checkboxes are native (keyboard and screen
 * reader friendly).
 */
export function RoleEditor({
  initialName,
  initialGrants,
  saving,
  error,
  onSave,
  onCancel,
}: {
  initialName: string;
  initialGrants: readonly string[];
  saving: boolean;
  error: string | null;
  onSave: (input: { name: string; permissions: string[] }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [values, setValues] = useState(() => toValues(initialGrants));
  const initialAdmin = [...toValues(initialGrants)].some(
    ([k, v]) => ADMIN_KEYS.has(k) && v !== 'none',
  );
  const removesAdmin =
    initialAdmin && ![...values].some(([k, v]) => ADMIN_KEYS.has(k) && v !== 'none');

  const set = (key: PermissionKey, value: Value) => {
    setValues((prev) => new Map(prev).set(key, value));
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ name: name.trim(), permissions: toGrants(values) });
      }}
    >
      <Field label="Nombre del rol">
        {(a11y) => (
          <Input
            {...a11y}
            required
            maxLength={100}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        )}
      </Field>

      {PERMISSION_GROUPS.map((group) => (
        <section key={group.title} className="space-y-3">
          <h3 className="font-semibold">{group.title}</h3>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {group.items.map(({ key, help }) => {
              const value = values.get(key) ?? 'none';
              return (
                <li
                  key={key}
                  className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="text-sm font-medium" id={`perm-${key}`}>
                      {permissionLabel(key)}
                    </p>
                    <p className="text-xs text-muted-foreground">{help}</p>
                  </div>
                  {isScopedPermission(key) ? (
                    <fieldset
                      aria-labelledby={`perm-${key}`}
                      className="flex shrink-0 rounded-lg border border-border p-0.5"
                    >
                      {SCOPE_OPTIONS.map((option) => (
                        <label
                          key={option}
                          className="cursor-pointer rounded-md px-2.5 py-1.5 text-xs has-[:checked]:bg-primary has-[:checked]:text-primary-foreground has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring"
                        >
                          <input
                            type="radio"
                            className="sr-only"
                            name={`perm-${key}`}
                            checked={value === option}
                            onChange={() => {
                              set(key, option);
                            }}
                          />
                          {SCOPE_LABELS[option]}
                        </label>
                      ))}
                    </fieldset>
                  ) : (
                    <label className="flex shrink-0 items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-5"
                        aria-labelledby={`perm-${key}`}
                        checked={value === 'on'}
                        onChange={(event) => {
                          set(key, event.target.checked ? 'on' : 'none');
                        }}
                      />
                      Permitido
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {removesAdmin && (
        <p role="status" className="flex gap-2 rounded-lg bg-warning/20 p-3 text-sm">
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          Estás quitando permisos de administración. Debe quedar al menos una persona activa que
          pueda gestionar usuarios y roles.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving} disabled={!name.trim()}>
          Guardar rol
        </Button>
      </div>
    </form>
  );
}
